import { type AxiosResponse } from 'axios';
import { call, put, takeLatest } from 'redux-saga/effects';

import * as addressActions from '~/actions/address.actions';
import { ActionKeys } from '~/actions/constants';
import { addSnackbarMessage } from '~/actions/snackbar.actions';
import * as userActions from '~/actions/user.actions';
import { type AddressBookInstances } from '~/reducers/address/address.reducer';
import { getAddressBookInstances } from '~/selectors/address.selectors';
import * as addressService from '~/services/address.service';
import * as APIAddress from '~/types/APIAddress';
import { sanitizeAddress } from '~/types/Address';

import { selectState } from '..';

function* fetchAddresses() {
  try {
    const response: AxiosResponse<{ results: APIAddress.JSONAPIAddress[] }> = yield call(
      addressService.getApiAddresses,
    );
    const parsedAddresses: addressActions.FetchAddressesPayload = {
      addresses: response.data.results.map((a) => APIAddress.fromJSON(a)),
    };
    yield put(addressActions.fetchAddressesSuccess(parsedAddresses));
  } catch (err) {
    yield put(addressActions.fetchAddressesError({ error: (err as Error).message }));
  }
}

export function* createAPIAddress(action: ReturnType<typeof addressActions.createAPIAddress>) {
  const { trackingReference } = action.payload;
  try {
    const jsonAddress = APIAddress.toJSON(sanitizeAddress(action.payload.address))!;
    const response: AxiosResponse<APIAddress.JSONAPIAddress> = yield call(
      addressService.createApiAddress,
      jsonAddress,
    );

    const address = APIAddress.fromJSON(response.data)!;
    const { isDefaultReturn, isDefaultSender } = address;
    if (isDefaultSender) yield put(userActions.updateUserDefaultAddress({ address }));

    if (isDefaultReturn) yield put(userActions.updateUserDefaultReturnAddress({ address }));

    yield put(
      addressActions.createAPIAddressSuccess({
        address,
        trackingReference,
      }),
    );

    if (action.payload.key && action.payload.addressBookName) {
      yield put(
        addressActions.addressBookSelectAddress(
          action.payload.addressBookName,
          action.payload.key,
          address.id!,
        ),
      );
    }
  } catch (err) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const responseData = (err as any).response.data;
    const errorMessage =
      typeof responseData === 'object'
        ? Object.values(responseData).join('\n')
        : Array.isArray(responseData)
        ? responseData.join('\n')
        : responseData;
    yield put(addressActions.createAPIAddressError({ error: errorMessage }));
    yield put(
      addSnackbarMessage({
        i18n: true,
        message: 'orders:address:errors:create',
        severity: 'error',
      }),
    );
  }
}

export function* editAPIAddress(action: ReturnType<typeof addressActions.editAPIAddress>) {
  try {
    const jsonAddress: APIAddress.JSONAPIAddress = APIAddress.toJSON(
      sanitizeAddress(action.payload.address),
    )!;
    const response: Awaited<ReturnType<typeof addressService.editApiAddress>> = yield call(
      addressService.editApiAddress,
      action.payload.id!,
      jsonAddress,
    );

    const address = APIAddress.fromJSON(response.data)!;
    const { isDefaultReturn, isDefaultSender } = address;

    if (isDefaultSender) yield put(userActions.updateUserDefaultAddress({ address }));

    if (isDefaultReturn) yield put(userActions.updateUserDefaultReturnAddress({ address }));

    yield put(addressActions.editAPIAddressSuccess({ address, id: action.payload.id }));
  } catch (err) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const responseData = (err as any).response.data;
    const errorMessage =
      typeof responseData === 'object'
        ? Object.values(responseData).join('\n')
        : Array.isArray(responseData)
        ? responseData.join('\n')
        : responseData;
    yield put(addressActions.editAPIAddressError({ error: errorMessage }));
  }
}

export function* deleteAPIAddress(action: ReturnType<typeof addressActions.deleteAPIAddress>) {
  try {
    yield call(addressService.deleteApiAddress, action.payload.id);

    yield put(addressActions.deleteAPIAddressSuccess(action.payload.id));
    if (action.payload.key) {
      const addressBookInstances: AddressBookInstances =
        yield selectState<AddressBookInstances>(getAddressBookInstances);
      for (const addressBookName in addressBookInstances) {
        yield put(
          addressActions.addressBookSelectDefaultAddress(addressBookName, action.payload.key),
        );
      }
    }
  } catch (err) {
    yield put(addressActions.deleteAPIAddressError({ error: (err as Error).message }));
  }
}

export default [
  takeLatest(ActionKeys.ADDRESSES_FETCH, fetchAddresses),
  takeLatest(ActionKeys.APIADDRESS_CREATE, createAPIAddress),
  takeLatest(ActionKeys.APIADDRESS_EDIT, editAPIAddress),
  takeLatest(ActionKeys.APIADDRESS_DELETE, deleteAPIAddress),
];
